import { useState, useRef, useCallback, useEffect } from "react"
import { useLocation } from "wouter"
import IPhoneDevice from "../components/IPhoneDevice"

// Redesign: reuses the exact same single-focus carousel design as
// /launched (same .lp-* classes, same hover pill / click-to-reveal
// description / arrows / dots interaction), instead of the previous card
// grid. "See You More" is the lead project and shows on the flat CSS
// iPhone mockup (IPhoneDevice), square-on.
//
// This was a real WebGL model for a while (IPhone3D, still in the repo
// and working if it's ever wanted again). It was dropped on purpose: it
// needed an 8.6MB model download, mobile Safari refused or dropped its
// context often enough to need a whole fallback path anyway, and it was
// sensitive to load order in ways that produced a black screen on a cold
// visit. The mockup renders identically on every device with none of
// that. The other two experiments — being external web tools rather than
// phone apps — use a plain screenshot, same as Launched does for any
// project without a video.
const PROJECTS = [
  {
    id: "mote",
    year: "2026",
    company: "iOS App",
    role: "Minimal expense tracker. Swipe up, jot it down",
    link: "https://www.ricetseng.com/mote.html",
    internal: false,
    title: "Mote.",
    description: "Swipe up and type what you spent — “6 coffee” — and Mote sorts it out. Your day comes back as a single receipt, and the year as one number with the categories behind it. The little things, accounted for.",
    clips: ["/mote-assets/record.mp4", "/mote-assets/receipt.mp4", "/mote-assets/insight.mp4"],
    inDevice: true,
  },
  {
    id: "lightwaymap",
    year: "2026",
    company: "iOS App",
    role: "Follow the light home. A safer way to walk",
    // A static page, not a React route — so it must be a real navigation
    // rather than a wouter push, or it lands on a blank screen.
    // internal: false routes it through window.open for exactly that.
    link: "https://www.ricetseng.com/lightwaymap.html",
    internal: false,
    title: "Lightway Map",
    description: "Sometimes the fastest route isn't the one you want to take. Lightway looks beyond distance — weighing street lighting, crossings, nearby open places and the environment around your walk — so you can choose what matters to you, see what's ahead, and follow the light home. Because safer means something different to everyone.",
    // Screen recordings, so they play inside the device mockup rather
    // than full-bleed like Remotion's.
    clips: ["/lightway-1.mp4", "/lightway-2.mp4"],
    inDevice: true,
  },
  {
    id: "remotion",
    year: "2026",
    company: "Motion Studio",
    role: "A real-time 3D twin of your iPhone, driven by the phone itself",
    link: "https://github.com/ricetseng/RemotionSite",
    internal: false,
    title: "Remotion",
    description: "Stream Core Motion attitude from a real iPhone to a photoreal WebGL twin at 60 Hz. Pitch, roll and yaw drive the model live over a local WebSocket relay, and the handset can broadcast its own screen onto the 3D device — turning a phone in your hand into a product mockup you can film.",
    // Plays full-bleed rather than inside a device mockup: the demo is
    // itself a recording of a device, so framing it in another one would
    // put a phone inside a phone. Two clips, played as a story — see
    // StoryBars below.
    clips: ["/remotion.mp4", "/remotion2-h264.mp4"],
    wide: true,
    credit: {
      lines: [
        "Remotion © 2026 Rice Tseng",
        "Includes open-source software © 2018 Brown University.",
      ],
      href: "https://github.com/brownhci/Remotion",
      label: "License ↗",
    },
  },
  {
    id: "seeyoumore",
    year: "2026",
    company: "iOS App",
    role: "A reminder of how precious time with the people you love truly is",
    link: "/seeyoumore",
    internal: true,
    title: "See You More",
    description: "How many more birthdays will you share with your mother? How many more times will you see your grandparents? See You More quietly calculates the moments you may have left with the people you love — turning quiet awareness into a reason to reach out today.",
    video: "/calendar.mov",
  },
  {
    id: "skill",
    year: "2026",
    company: "SKILLS.MD",
    // A real one-line description rather than the category label
    // "AI × Design" — which said nothing the tag above it didn't, and
    // left the two experiments looking identical to each other. Matches
    // the tagline shape See You More already uses in this slot.
    role: "A material time machine for Apple UI eras",
    link: "https://ricetseng.com/skill",
    internal: false,
    title: "Apple UI materials design skill",
    description: "A material time machine for Apple UI eras. Explore every era from Classic Mac to Liquid Glass — and apply their depth, texture and motion to modern interfaces.",
    gif: "/MacBook Pro 14_01.png",
    wide: true,
  },
  {
    id: "portfolio-spector",
    year: "2026",
    company: "Interview AI Tool",
    role: "AI critique from a senior hiring leader's lens",
    link: "https://portfolio-spector.vercel.app/",
    internal: false,
    title: "Portfolio Spector",
    description: "AI critique from a senior hiring leader's lens. Submit your portfolio URL and get 7 scored dimensions, specific gaps, and the exact changes that move the needle — built to get you more interviews.",
    gif: "/MacBook Pro 14_02.png",
    wide: true,
  },
]

// Node.contains() throws "parameter 1 is not of type 'Node'" if handed
// anything that isn't a Node, and a mouseleave's relatedTarget is null
// whenever the pointer exits toward browser chrome or off the window
// entirely. Unguarded, that throws inside React's event dispatch and
// surfaces as an uncaught runtime error.
const containsNode = (parent, target) =>
  target instanceof Node && !!parent && parent.contains(target)

export default function AILab() {
  const [, navigate] = useLocation()
  const [idx, setIdx]         = useState(0)
  const [hovered, setHovered] = useState(false)
  const [overArrow, setOverArrow] = useState(false)
  const [popup, setPopup]     = useState(false)
  const [panelPos, setPanelPos]           = useState(null)
  const [displayedText, setDisplayedText] = useState("")
  const [typeDone, setTypeDone]           = useState(false)
  const deviceRef    = useRef(null)
  const btnRef       = useRef(null)
  const infoPanelRef = useRef(null)
  const rafRef       = useRef(null)
  const timerRef     = useRef(null)
  const n = PROJECTS.length
  const p = PROJECTS[idx]

  // ── Story playback (Remotion) ──
  // Two clips play back to back on a loop, with a segmented bar showing
  // position — the Instagram story pattern. The video lives in the
  // centre column and the bar in the left one, so both the element ref
  // and the state have to live up here where they're common ancestors.
  const storyRef = useRef(null)
  const storyRafRef = useRef(null)
  const [clip, setClip] = useState(0)
  const [clipProgress, setClipProgress] = useState(0)
  const clips = p.clips

  // Back to the first clip whenever the slide changes, so returning to
  // this project doesn't resume halfway through the second video.
  useEffect(() => {
    setClip(0)
    setClipProgress(0)
  }, [p.id])

  // Progress is sampled per animation frame rather than from the video's
  // own timeupdate event, which only fires about four times a second and
  // makes the bar visibly step instead of sweep.
  useEffect(() => {
    if (!clips) return undefined
    const tick = () => {
      const v = storyRef.current
      if (v && v.duration > 0) setClipProgress(v.currentTime / v.duration)
      storyRafRef.current = requestAnimationFrame(tick)
    }
    storyRafRef.current = requestAnimationFrame(tick)
    return () => cancelAnimationFrame(storyRafRef.current)
  }, [clips, clip])

  const goToClip = (i) => {
    setClip(i)
    setClipProgress(0)
  }

  // Typewriter effect when panel opens
  useEffect(() => {
    clearTimeout(timerRef.current)
    if (popup) {
      setDisplayedText("")
      setTypeDone(false)
      let i = 0
      const text = p.description
      const tick = () => {
        i++
        setDisplayedText(text.slice(0, i))
        if (i < text.length) timerRef.current = setTimeout(tick, 16)
        else setTypeDone(true)
      }
      timerRef.current = setTimeout(tick, 80)
    } else {
      setDisplayedText("")
      setTypeDone(false)
    }
    return () => clearTimeout(timerRef.current)
  }, [popup, p.id])

  // Direct DOM update via RAF — pill follows cursor
  const handleMouseMove = useCallback((e) => {
    if (!deviceRef.current || !btnRef.current) return
    const rect = deviceRef.current.getBoundingClientRect()
    const x = e.clientX - rect.left
    const y = e.clientY - rect.top
    if (rafRef.current) cancelAnimationFrame(rafRef.current)
    rafRef.current = requestAnimationFrame(() => {
      if (btnRef.current) {
        btnRef.current.style.left = `${x}px`
        btnRef.current.style.top  = `${y}px`
      }
    })
  }, [])

  useEffect(() => () => { if (rafRef.current) cancelAnimationFrame(rafRef.current) }, [])

  const go = (d) => {
    setHovered(false)
    setPopup(false)
    setIdx(i => (i + d + n) % n)
  }

  const openLink = () => {
    if (p.internal) navigate(p.link)
    else window.open(p.link, "_blank", "noopener noreferrer")
  }

  return (
    <div className="page-panel lp-panel">
      <div className="lp-page">

        {/* ── Left metadata ── */}
        <aside className="lp-meta lp-meta--lab" key={p.id}>
          <span className="lp-meta-tag">{p.year}・{p.company}</span>
          <h2 className="lp-meta-title">{p.title}</h2>
          <span className="lp-meta-role">{p.role}</span>
          {/* No URL line here. It duplicated the button below — both went
              to the same place — and the book block on /writing this is
              modelled on is year / title / description / CTA with no URL
              of its own. The destination still shows in the status bar
              on hover, and on the button's title attribute. */}
          {/* Story bars sit ALONGSIDE the View button, not instead of it.
              The bar is a progress readout and clip switcher — never a
              link — so a project with clips still needs the button to get
              through to the actual thing. */}
          {clips && (
            <div className="lp-story" role="group" aria-label="Demo clips">
              <div className="lp-story-bars">
                {clips.map((src, i) => (
                  <button
                    key={src}
                    type="button"
                    className="lp-story-bar"
                    onClick={() => goToClip(i)}
                    aria-label={`Clip ${i + 1} of ${clips.length}`}
                    aria-current={i === clip}
                  >
                    <span
                      className="lp-story-bar-fill"
                      style={{
                        // Clips already played sit at 100% rather than
                        // resetting, so the bar reads as a position in
                        // the whole sequence, not just the current clip.
                        transform: `scaleX(${i < clip ? 1 : i === clip ? clipProgress : 0})`,
                      }}
                    />
                  </button>
                ))}
              </div>

            </div>
          )}

          {/* No URL line here. It duplicated this button — both went to
              the same place — and the book block on /writing this is
              modelled on is year / title / description / CTA with no URL
              of its own. The destination still shows in the status bar on
              hover, and on the button's title attribute. */}
          <button className="lp-meta-link" onClick={openLink} title={p.link}>
            View →
          </button>

          {/* Attribution last — it's fine print, so it sits below the call
              to action rather than between it and the bars. */}
          {p.credit && (
            <p className="lp-story-credit">
              {(p.credit.lines || []).map((line) => (
                <span key={line}>{line}</span>
              ))}
              <a href={p.credit.href} target="_blank" rel="noopener noreferrer">
                {p.credit.label}
              </a>
            </p>
          )}
        </aside>

        {/* ── Device GIF/3D-phone + flanking arrows ── */}
        <div className="lp-center">
          <div className="lp-col">
            <div
              ref={deviceRef}
              className={`lp-device${hovered ? " lp-device--hovered" : ""}`}
              onMouseEnter={() => setHovered(true)}
              onMouseLeave={(e) => {
                if (containsNode(infoPanelRef.current, e.relatedTarget)) return
                setHovered(false)
                setPopup(false)
                setPanelPos(null)
              }}
              onMouseMove={handleMouseMove}
              onClick={(e) => {
                if (popup) { setPopup(false); setPanelPos(null); return }
                const rect = deviceRef.current.getBoundingClientRect()
                setPanelPos({ x: e.clientX - rect.left, y: e.clientY - rect.top })
                setPopup(true)
              }}
            >
              <button className="lp-nav lp-nav--left" onClick={(e) => { e.stopPropagation(); go(-1) }} onMouseEnter={() => setOverArrow(true)} onMouseLeave={() => setOverArrow(false)} aria-label="Previous">
                <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                  <path d="M10 3L5 8L10 13" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </button>

              {/* Flat CSS mockup on every viewport, not just as a mobile
                  fallback — same component /launched uses, square-on via
                  `front`. No WebGL means no context to lose, no 8.6MB
                  model to fetch, and none of the load-order fragility the
                  3D version carried. The persistent-canvas scaffolding
                  that used to live here went with it: nothing needs to
                  survive a carousel navigation any more, so this is just
                  a normal keyed frame like the image slides. */}
              <div className="lp-frame" key={p.id}>
                {p.video ? (
                  <IPhoneDevice src={p.video} front />
                ) : clips && p.inDevice ? (
                  /* Screen recordings — they belong inside the device
                     mockup. Same story mechanics as the full-bleed case
                     below: the parent owns which clip is showing and
                     reads progress from the element, so it hands down
                     its ref and turns looping off. */
                  <IPhoneDevice
                    key={clips[clip]}
                    src={clips[clip]}
                    front
                    videoRef={storyRef}
                    loop={false}
                    onEnded={() => goToClip((clip + 1) % clips.length)}
                  />
                ) : clips ? (
                  <video
                    ref={storyRef}
                    key={clips[clip]}
                    className="lp-img lp-img--wide"
                    src={clips[clip]}
                    autoPlay
                    muted
                    playsInline
                    /* Deliberately NOT loop: each clip has to end so the
                       next one can start. The sequence loops instead, by
                       wrapping back to the first. */
                    onEnded={() => goToClip((clip + 1) % clips.length)}
                    aria-label={`${p.title} — clip ${clip + 1} of ${clips.length}`}
                  />
                ) : (
                  <img src={p.gif} alt={p.title} className={`lp-img${p.wide ? " lp-img--wide" : ""}`} />
                )}
              </div>

              {!p.video && !clips && !p.wide && <div className="lp-gradient" />}

              {/* Cursor-following "View more" pill */}
              <div
                ref={btnRef}
                className={`lp-cursor-btn${hovered && !overArrow ? " lp-cursor-btn--on" : ""}`}
              >
                View more ↗
              </div>

              {/* Info panel — anchored to click position, bottom-right of pill */}
              {popup && panelPos && (
                <div
                  ref={infoPanelRef}
                  className="lp-info-panel"
                  style={{ left: `${panelPos.x}px`, top: `${panelPos.y}px` }}
                  onMouseLeave={(e) => {
                    if (!containsNode(deviceRef.current, e.relatedTarget)) {
                      setHovered(false)
                      setPopup(false)
                      setPanelPos(null)
                    }
                  }}
                  onClick={e => { e.stopPropagation(); setPopup(false); setPanelPos(null) }}
                >
                  <p className="lp-info-text">
                    {displayedText}
                    {!typeDone && <span className="lp-info-cursor" />}
                  </p>
                </div>
              )}

              <button className="lp-nav lp-nav--right" onClick={(e) => { e.stopPropagation(); go(1) }} onMouseEnter={() => setOverArrow(true)} onMouseLeave={() => setOverArrow(false)} aria-label="Next">
                <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                  <path d="M6 3L11 8L6 13" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </button>
            </div>

            {/* Dots only — flows directly below device. The caption that
                used to sit here repeated the tag, title and role already
                shown in .lp-meta, so it was dropped. */}
            <div className="lp-footer">
              <div className="lp-dots">
                {PROJECTS.map((_, i) => (
                  <button
                    key={i}
                    className={`lp-dot${i === idx ? " lp-dot--active" : ""}`}
                    onClick={() => { setHovered(false); setIdx(i) }}
                    aria-label={`Project ${i + 1}`}
                  />
                ))}
              </div>
            </div>
          </div>
        </div>

      </div>
    </div>
  )
}
